import {
	BI_REPORTS_CONFIG,
	BI_REPORTS_MESSAGES,
} from 'components/reports/biReportEmbed/utils/biReports';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
	IBiEmbedConfig,
	IBiEmbedResponse,
	IBiEmbedState,
} from '../biReportEmbed.types';
import { cleanupBiEmbed, openSpecificReport } from '../services/biEmbedService';

let idCounter = 0;
const useUniqueId = (): string => {
	const [id] = useState(() => `${++idCounter}`);
	return id;
};

export const useBiEmbed = (config: IBiEmbedConfig): IBiEmbedResponse => {
	const uniqueId = useUniqueId();

	const containerId = `${BI_REPORTS_CONFIG.CONTAINER_ID_PREFIX}${uniqueId}`;

	const [state, setState] = useState<IBiEmbedState>({
		isLoading: false,
		error: null,
		isEmbedded: false,
	});

	const containerRef = useRef<HTMLDivElement>(null);
	const retryCountRef = useRef<number>(0);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const validateEmbedPrerequisites = useCallback((): {
		isValid: boolean;
		error?: string;
	} => {
		if (!config.reportToken) {
			return {
				isValid: false,
				error: BI_REPORTS_MESSAGES.MSG_BI_TOKEN_NOT_CONFIGURED,
			};
		}

		if (!containerRef.current) {
			return {
				isValid: false,
				error: BI_REPORTS_MESSAGES.MSG_BI_CONTAINER_NOT_FOUND,
			};
		}

		return {
			isValid: true,
		};
	}, [config.reportToken]);

	const setLoadingState = useCallback((isLoading: boolean) => {
		setState(prev => ({
			...prev,
			isLoading,
			error: isLoading ? null : prev.error,
			isEmbedded: isLoading ? false : prev.isEmbedded,
		}));
	}, []);

	const setErrorState = useCallback((error: string) => {
		setState(prev => ({
			...prev,
			isLoading: false,
			isEmbedded: false,
			error,
		}));
	}, []);

	const setSuccessState = useCallback(() => {
		setState(prev => ({
			...prev,
			isLoading: false,
			isEmbedded: true,
			error: null,
		}));
	}, []);

	const embedBi = useCallback(async () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		const validation = validateEmbedPrerequisites();
		if (!validation.isValid) {
			setErrorState(validation.error!);
			return;
		}

		setLoadingState(true);

		try {
			if (containerRef.current) {
				containerRef.current.id = containerId;
			}

			const embedPromise = openSpecificReport(
				config.reportToken,
				containerId
			);

			const timeoutPromise = new Promise<never>((_, reject) => {
				timeoutRef.current = setTimeout(() => {
					reject(new Error(BI_REPORTS_MESSAGES.MSG_BI_LOAD_ERROR));
				}, BI_REPORTS_CONFIG.SCRIPT_LOAD_TIMEOUT);
			});

			await Promise.race([embedPromise, timeoutPromise]);

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}

			setSuccessState();
			retryCountRef.current = 0;
		} catch (error) {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}

			const errorMessage =
				error instanceof Error
					? error.message
					: BI_REPORTS_MESSAGES.MSG_BI_LOAD_ERROR;

			setErrorState(errorMessage);
		}
	}, [
		config.reportToken,
		containerId,
		validateEmbedPrerequisites,
		setLoadingState,
		setErrorState,
		setSuccessState,
	]);

	const resetEmbed = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}

		cleanupBiEmbed(containerId);

		setState({
			isLoading: false,
			error: null,
			isEmbedded: false,
		});

		retryCountRef.current = 0;
	}, [containerId]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			cleanupBiEmbed(containerId);
		};
	}, [containerId]);

	return {
		...state,
		embedBi,
		resetEmbed,
		containerRef,
	};
};
